"use client";

import { Fragment, type CSSProperties } from "react";
import { Badge } from "@/components/ui/badge";
import { useMentionResolver } from "./mention-resolver";

const MENTION_BADGE_STYLE: CSSProperties = {
  ["--gradient-color" as string]: "#2C7EFF",
};

const MENTION_TOKEN_RE = /<@([0-9a-f-]{36})>/gi;

type Segment =
  | { type: "text"; value: string }
  | { type: "mention"; id: string };

function tokenize(content: string): Segment[] {
  const segments: Segment[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  MENTION_TOKEN_RE.lastIndex = 0;
  while ((m = MENTION_TOKEN_RE.exec(content))) {
    if (m.index > last) {
      segments.push({ type: "text", value: content.slice(last, m.index) });
    }
    segments.push({ type: "mention", id: m[1] });
    last = m.index + m[0].length;
  }
  if (last < content.length) {
    segments.push({ type: "text", value: content.slice(last) });
  }
  return segments;
}

export function MentionText({ content }: { content: string }) {
  const { members, isLoading } = useMentionResolver();
  const segments = tokenize(content);

  return (
    <>
      {segments.map((seg, idx) => {
        if (seg.type === "text") {
          const lines = seg.value.split("\n");
          return (
            <Fragment key={idx}>
              {lines.map((line, i) => (
                <Fragment key={i}>
                  {line}
                  {i < lines.length - 1 && <br />}
                </Fragment>
              ))}
            </Fragment>
          );
        }
        const member = members.get(seg.id.toLowerCase());
        const name = member
          ? `${member.first_name ?? ""} ${member.last_name ?? ""}`.trim() ||
            member.email
          : null;

        if (!name && isLoading) {
          return (
            <Badge
              key={idx}
              variant="linear"
              size="xs"
              className="text-primary dark:text-[#83b9ff] align-baseline"
              style={MENTION_BADGE_STYLE}
            >
              <span className="inline-block h-3 w-16 rounded-sm bg-primary/20 dark:bg-[#83b9ff]/20 animate-pulse" />
            </Badge>
          );
        }

        return (
          <Badge
            key={idx}
            variant="linear"
            size="xs"
            className="text-primary dark:text-[#83b9ff] align-baseline"
            style={MENTION_BADGE_STYLE}
          >
            @{name ?? seg.id}
          </Badge>
        );
      })}
    </>
  );
}
